<script setup>

    import { computed } from 'vue'
    import { util } from '@/Helpers'
    import moment from 'moment'
    import { storeToRefs } from 'pinia'
    import SearchScheduleViewBlock from '@/Components/Caregiver/SearchScheduleViewBlock.vue'
    import { useCaregiverSingleStore } from '@/Stores'

    const caregiverSingleStore = useCaregiverSingleStore()
    const { userFocusedOn } = storeToRefs(caregiverSingleStore)

    const props = defineProps({
        schedule_start_date: {
            type: String,
            required: true
        },
        block: {
            type: Object,
            required: true
        },
        care_schedule_search_match: {
            type: Object,
            required: true
        },
        displayMode: {
            type: String,
            default: 'search',
            validator: (value) => ['default', 'search', 'search_verbose'].includes(value)
        }
    })

    const scheduleMatchStyles = computed(() => {
        const baseClasses = 'backdrop-opacity-75 backdrop-blur-sm text-opacity-100 border-opacity-50'
        const typeStyles = {
            'full': 'bg-opacity-70 bg-[#A4CA91] border border-lifeworx-green-500 text-lifeworx-green-600',
            'partial': 'bg-opacity-70 bg-[#E6A439] border border-lifeworx-orange-600 text-lifeworx-orange-700',
            'none': 'bg-opacity-70 bg-lifeworx-red-200 border border-lifeworx-red-500 text-lifeworx-red-500'
        }
        return `${baseClasses} ${typeStyles[props.care_schedule_search_match.match_type]}`
    })

    const isCurrentOrFutureWeek = computed(() => {
        const lastSunday = moment(util.date.lastSunday(), util.date.date_format)
        const startDate = moment(props.schedule_start_date, util.date.date_format)

        return startDate.isSameOrAfter(lastSunday);
    })

    const reasons = computed(() => {
        if (props.care_schedule_search_match.match_type === 'none') {
            return props.care_schedule_search_match.no_match_reasons;
        } else if (props.care_schedule_search_match.match_type === 'partial') {
            return [ 'Partial Match' ];
        } else if (props.care_schedule_search_match.match_type === 'full') {
            return [ 'Good Match!' ];
        }
        return [];
    })

</script>

<template>
    <li
        class="relative flex group justify-center ml-05 mr-2"
        :class="[
            `col-start-${util.date.dateDiff(schedule_start_date, block.start_date) + 1}`,
            {
                'z-50': props.displayMode === 'search',
                'z-40': userFocusedOn == null && props.displayMode === 'search_verbose',
                'z-30': userFocusedOn !== null && props.displayMode === 'search_verbose',
            }
        ]"
        :style="`grid-row: ${util.schedule.startWithTz(block.start_time)} / span ${util.schedule.span(block.duration)}`"
    >
        <SearchScheduleViewBlock
            :isCurrentOrFutureWeek="isCurrentOrFutureWeek"
            :block="block"
            :reasons="reasons"
            :size="util.schedule.sizeOf(block)"
            :class="[
                'group absolute flex inset-0.5 flex-col overflow-y-auto text-xs whitespace-nowrap cursor-default',
                {
                    'rounded-t-lg': block.continues,
                    'rounded-b-lg': block.continuation,
                    'rounded-lg': !block.continues && !block.continuation,
                    'p-0.5 border-2': ['3xs', '2xs', 'xs'].includes(util.schedule.sizeOf(block)),
                    'p-1 border-2': ['sm', 'md', 'lg', 'xl', '2xl'].includes(util.schedule.sizeOf(block)),
                    'backdrop-blur-sm bg-opacity-20': block.id && block.overlaps,
                    'opacity-60': userFocusedOn !== null && props.displayMode === 'search_verbose',
                },
                scheduleMatchStyles
            ]"
        />
    </li>
</template>
